import type { Domain } from "@/lib/api";
import { DOMAIN_LABEL, DOMAIN_ORDER, type Filters } from "@/lib/overview";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { chipShapeRaw, pressableRaw } from "./pressable";

interface FilterBarProps {
  filters: Filters;
  counts: { all: number; byDomain: Record<Domain, number> };
  onDomain: (domain: Filters["domain"]) => void;
  onToggle: (facet: "waiting" | "hasDate" | "touchedThisWeek") => void;
}

const chip = css(pressableRaw, chipShapeRaw);

/** Domain chips: same states, but the on-state uses the domain's own color and fill. */
const DOMAIN_CHIP: Record<Domain, string> = {
  cetec: css(pressableRaw, chipShapeRaw, {
    "&[aria-pressed=true]": {
      color: "domain.cetec",
      borderColor: "domain.cetec",
      bg: "domain.cetec.fill",
    },
    "&[aria-pressed=true]:hover": { bg: "domain.cetec.fill", borderColor: "domain.cetec" },
    "&[aria-pressed=true]:active": { bg: "domain.cetec.fill" },
  }),
  mkbd: css(pressableRaw, chipShapeRaw, {
    "&[aria-pressed=true]": {
      color: "domain.mkbd",
      borderColor: "domain.mkbd",
      bg: "domain.mkbd.fill",
    },
    "&[aria-pressed=true]:hover": { bg: "domain.mkbd.fill", borderColor: "domain.mkbd" },
    "&[aria-pressed=true]:active": { bg: "domain.mkbd.fill" },
  }),
  personal: css(pressableRaw, chipShapeRaw, {
    "&[aria-pressed=true]": {
      color: "domain.personal",
      borderColor: "domain.personal",
      bg: "domain.personal.fill",
    },
    "&[aria-pressed=true]:hover": { bg: "domain.personal.fill", borderColor: "domain.personal" },
    "&[aria-pressed=true]:active": { bg: "domain.personal.fill" },
  }),
};

const FACETS: Array<{ key: "waiting" | "hasDate" | "touchedThisWeek"; label: string }> = [
  { key: "waiting", label: "waiting" },
  { key: "hasDate", label: "has date" },
  { key: "touchedThisWeek", label: "touched this week" },
];

export function FilterBar({ filters, counts, onDomain, onToggle }: FilterBarProps) {
  return (
    <Box as="section" display="flex" alignItems="center" gap="16" flexWrap="wrap">
      <Box display="flex" gap="6">
        <button
          type="button"
          aria-pressed={filters.domain === "all"}
          className={chip}
          onClick={() => onDomain("all")}
        >
          All · {counts.all}
        </button>
        {DOMAIN_ORDER.map((d) => (
          <button
            key={d}
            type="button"
            aria-pressed={filters.domain === d}
            className={DOMAIN_CHIP[d]}
            onClick={() => onDomain(d)}
          >
            {DOMAIN_LABEL[d]} · {counts.byDomain[d]}
          </button>
        ))}
      </Box>
      <Box w="1" h="20" bg="border" />
      <Box display="flex" gap="6">
        {FACETS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={filters[key]}
            className={chip}
            onClick={() => onToggle(key)}
          >
            {label}
          </button>
        ))}
      </Box>
      <Text textStyle="mono.xs" color="text.subtlest" ml="auto">
        filters apply to the calendar and the list
      </Text>
    </Box>
  );
}
